import React, { useState, useEffect, useCallback } from "react";
import { setTitle } from "../../helpers/MetaTag";
import Button from "../../components/form/Button";
import Select from "../../components/form/Select";
import Search from "../../components/form/Search";
import Table from "../../components/elements/table/Table";
import moment from "moment";
import { DragDropContext } from "react-beautiful-dnd";
import { reorder } from "../../helpers";
import toastr from "toastr";
import { useSelector } from "react-redux";

import { postData } from "../../services/api";
import { useTranslation } from "react-i18next";
import Pagination from "../../components/Common/Pagination";
import { useDebounce } from "../../helpers/useDebounce";
import { Tooltip } from "react-tooltip";
import AddEditPlanFeaturePopup from "../../components/popup/AddEditPlanFeaturePopup";
import { getStatusFilter } from "../../constant/Constant";
import MultipleButtonAction from "../../components/elements/multipleButtonAction/MultipleButtonAction";

const PlanFeatures = () => {
  setTitle("Succession Planning Software for your Business with Succession Now | Plan Features");
  const { t } = useTranslation();
  const status_filter = getStatusFilter(t);
  const role = localStorage.getItem("role");
  const user = useSelector((state) => state.auth?.user);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 500);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortValue, setSortValue] = useState(-1);
  const [sortQuery, setSortQuery] = useState("");
  const [status, setStatus] = useState("");
  const [headingType, setHeadingType] = useState("");
  const [noOfPage, setNoOfPage] = useState(0);
  const [plans, setPlans] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [startDataCount, setStartDataCount] = useState(0);
  const [endDataCount, setEndDataCount] = useState(0);
  const [actionValue, setActionValue] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);
  const [isOpen, setIsopen] = useState({
    _id: null,
    open: false,
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);

  const convartString = (string) => {
    let wordsArray = string.split("-");
    let capitalizedWords = wordsArray.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    let resultString = capitalizedWords.join(" ");
    return resultString;
  };

  useEffect(() => {
    if (sortKey && sortValue) {
      setSortQuery({ [sortKey]: sortValue });
    } else {
      setSortQuery("");
    }
  }, [sortKey, sortValue]);

  const extraData = [
    role && (role === "master-admin" || user?.permission?.includes("planfeature_edit"))
      ? {
          _id: 1,
          label: t("edit"),
          icon: "fa-pen-to-square",
          link: "/",
          type: "button",
          standout: false,
          buttonType: "edit-user-info",
          ordering: 1,
          isVisabled: 1,
        }
      : null,
  ].filter(Boolean);

 const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await postData("plan-feature/list", {
        keyword: search,
        limit: limit,
        offset: offset,
        sortQuery: sortQuery,
        status: status,
        headingType: headingType,
      });
      if (result.data) {
        setPlans(result.data);
      }
      setTotalDataCount(result.count);
    } catch (err) {
      console.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, limit, offset, sortQuery, status, headingType]);

  useEffect(() => {
    loadList();
  }, [loadList, isUpdate]);

  const tableData =
    plans &&
    Array?.isArray(plans) &&
    plans.length > 0 &&
    plans?.map((value, key) => {
      return {
        _id: value?._id,
        rowData: [
          {
            _id: 1,
            width: 40,
            type: "text",
            data: value?.name || "N/A",
          },
          // { _id: 2, width: 20, type: "text", data: value?.code },
          {
            _id: 3,
            width: 20,
            type: "text",
            data: value?.fieldType || "N/A",
          },
          {
            _id: 4,
            width: 20,
            type: "text",
            data: value?.headingType ? convartString(value?.headingType) : "N/A",
          },
          {
            _id: 5,
            width: 10,
            type: "text",
            data: value && value?.created_at ? moment(new Date(value?.created_at)).format("ll") : "N/A",
          },
          {
            _id: 6,
            width: 5,
            type: "status",
            statusLabel: value?.status && value?.status === 1 ? t("active") : t("deactivated"),
            statusType: value?.status && value?.status === 1 ? "success" : "warning",
          },
          {
            _id: 7,
            width: 5,
            type: "action",
            statusLabel: value?.status && value?.status === 1 ? t("active") : t("deactivated"),
            statusType: value?.status && value?.status === 1 ? "success" : "warning",
            data: [{ "edit-user-info": 1 }],
          },
        ],
      };
    });

  useEffect(() => {
    setNoOfPage(Math.ceil(totalDataCount / limit));
    const startItem = Number(totalDataCount > 0 ? 1 : 0);
    const endItem = Math.min(startItem + limit - 1, totalDataCount);
    setStartDataCount(startItem);
    setEndDataCount(endItem);
  }, [limit, totalDataCount]);

  const handlePageClick = (event) => {
    const newOffset = (event.selected * limit) % totalDataCount;
    console.log(`User requested page number ${event.selected}, which is offset ${newOffset}`);
    setOffset(newOffset);
    const startItem = Number(newOffset + 1);
    const endItem = Math.min(startItem + limit - 1, totalDataCount);
    setStartDataCount(startItem);
    setEndDataCount(endItem);
    setSelectedRow([]);
  };

  const tableHeadData = [
    {
      _id: 1,
      width: 40,
      name: t("name"),
      value: "name",
      align: "left",
      isSort: false,
      isFilter: false,
    },
    // { _id: 2, width: 20, name: t("code"), value: "code", align: "left", isSort: false, isFilter: false },
    {
      _id: 3,
      width: 20,
      name: t("field_type"),
      value: "fieldType",
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: 4,
      width: 20,
      name: t("heading_type"),
      value: "headingType",
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: 5,
      width: 10,
      name: t("created"),
      value: "created_at",
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: 6,
      width: 5,
      name: t("status"),
      value: "status",
      align: "left",
      isSort: false,
      isFilter: true,
      filterData: status_filter || [],
      onFilter: (data) => {
        if (data && Array.isArray(data) && data.length === 1) {
          setStatus(data[0]?.value);
        } else {
          setStatus("");
        }
        setOffset(0);
      },
    },
    {
      _id: 7,
      width: 5,
      name: t("action"),
      value: "",
      align: "right",
      isSort: false,
      isFilter: false,
    },
  ];

  const handleOnDragEnd = async (result) => {
    const items = reorder(plans, result.source.index, result.destination.index);
    setPlans(items);

    const res = await postData("position/ordering", {
      items: items,
    });
    if (res.status && res.status === 200) {
      toastr.success(res.message);
    } else {
      toastr.error(res.message);
    }
  };

  useEffect(() => {
    if (actionValue && actionValue.button_type === "status") {
      const loadStatusChange = async () => {
        setIsUpdate(false);
        try {
          const statusData = await postData("plan-feature/status-change", {
            id: actionValue?.row_id,
            status: actionValue?.current_status,
          });
          if (statusData.data) {
            setPlans((prevPlans) =>
              prevPlans.map((it) => {
                if (it?._id === statusData.data?._id) {
                  return {
                    ...it,
                    status: statusData.data?.status,
                  };
                } else {
                  return it;
                }
              })
            );
            setIsUpdate(true);
          } else {
            toastr.error(statusData.message);
          }
        } catch (error) {
          toastr.error(error.message);
        }
      };
      loadStatusChange();
    } else if (actionValue && actionValue.button_type === "delete") {
      const loadDeleteRecord = async () => {
        setIsUpdate(false);
        try {
          const statusData = await postData("plan-feature/delete", {
            id: actionValue?.row_id,
          });
          if (statusData.data) {
            setPlans((prevPlans) => {
              const updatedPlans = prevPlans.filter((item) => item._id !== actionValue?.row_id);
              return updatedPlans;
            });
            // const index = plans.findIndex((item) => item._id === actionValue?.row_id);
            // if (index !== -1) {
            //   plans.splice(index, 1);
            //   setPlans(
            //     plans.map((it) => {
            //       return it;
            //     })
            //   );
            // }
            setIsUpdate(true);
          } else {
            toastr.error(statusData.message);
          }
        } catch (error) {
          toastr.error(error.message);
        }
      };
      loadDeleteRecord();
    } else if (actionValue && actionValue.button_type === "edit-user-info") {
      setIsopen({ open: true, _id: actionValue?.row_id });
    }
  }, [actionValue]);

  return (
    <>
      <div className="border bg-white border-gray-200 p-4 shadow-md rounded-xl">
        <div className="relative flex justify-between items-center mb-3">
          <div className="text-2xl font-bold">{t("plan_features")}</div>
          {role && (role === "master-admin" || (user.permission && user.permission.filter((item) => item === "planfeature_create").length > 0)) && (
            <div className="sm:ml-auto flex justify-end items-center" data-tooltip-id={`buttonAddPlan${1}`} data-tooltip-place="right">
              <Button
                buttonType={"button"}
                buttonIcon={"fa-light fa-plus"}
                buttonIconPosition={"left"}
                buttonLabel={t("add")}
                buttonHasLink={false}
                functions={() => setIsopen({ _id: null, open: true })}
              />
              <Tooltip
                id={`buttonAddPlan${1}`}
                className="!text-xs !bg-teal-500 z-50 rounded py-2 px-2"
                render={() => (
                  <div className="gap-x-1 gap-y-1">
                    <div className="text-[13px] font-normal leading-none flex items-center">
                      <div className="font-semibold">Add New Plan Feature</div>
                    </div>
                  </div>
                )}
              />
            </div>
          )}
        </div>
        <div className="relative flex gap-3 items-center mb-4 flex-wrap sm:flex-nowrap gap-y-2">
          <div className="relative">
            <Select
              xPlacement={"left"}
              dropdownButtonClass={"!bg-white"}
              selectedValue={limit}
              dropdownData={[
                { _id: 1000000, label: t("all_items"), value: 1000000 },
                ...[
                  { _id: 10, label: "10 " + t("items"), value: 10 },
                  { _id: 20, label: "20 " + t("items"), value: 20 },
                  { _id: 30, label: "30 " + t("items"), value: 30 },
                  { _id: 50, label: "50 " + t("items"), value: 50 },
                  { _id: 100, label: "100 " + t("items"), value: 100 },
                ],
              ]}
              getSelectedValue={(e) => {
                setLimit(e.value);
                setOffset(0);
                setCurrentPage(0);
                setSelectedRow([]);
              }}
            />
          </div>
          <div className="relative w-48">
            <Search
              placeholder={"Search"}
              setInput={setKeyword}
              valueReset={() => {
                setOffset(0);
                setCurrentPage(0);
                setSelectedRow([]);
              }}
            />
          </div>
          <div className="relative w-48">
            <Select
              xPlacement={"right"}
              dropdownButtonClass={"!bg-white"}
              dropdownClass="w-48"
              transitionClass={"!w-auto"}
              selectedValue={headingType}
              dropdownData={[
                { _id: "", label: t("select_heading_type") },
                {
                  _id: "key-features",
                  label: t("key_features"),
                  value: "key-features",
                },
                {
                  _id: "number-of-plans",
                  label: t("number_of_plans"),
                  value: "number-of-plans",
                },
                {
                  _id: "additional-tools",
                  label: t("additional_tools"),
                  value: "additional-tools",
                },
              ]}
              getSelectedValue={(e) => {
                setHeadingType(e.value);
                setOffset(0);
                setCurrentPage(0);
                setSelectedRow([]);
              }}
            />
          </div>
          <div className="relative">
            <MultipleButtonAction
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
              list={plans}
              setList={setPlans}
              loadList={loadList}
              API_URL_STATUS={"plan-feature/status-change"}
              API_URL_DELETE={"plan-feature/delete"}
            />
          </div>
        </div>
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <div className="w-full bg-white rounded-md shadow overflow-hidden">
            <Table
              tableData={tableData}
              tableHeadData={tableHeadData}
              totalRecord={totalDataCount}
              loading={loading}
              getSortValue={setSortValue}
              getSortKey={setSortKey}
              // {...(role &&
              // (role === "master-admin" ||
              //   (user.permission &&
              //     user.permission.filter((item) => item === "planfeature_edit")
              //       .length > 0))
              //   ? { editUrl: "/edit-planfeature" }
              //   : "")}
              isStatusUpdate={true}
              isDeleteEnabled={true}
              isDraggable={false}
              extraData={extraData}
              getActionValue={(obj) => setActionValue(obj)}
              tableImage={false}
              // {...inputAttr}
              tableClasses={"min-h-[400px]"}
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
              selectable={true}
            />
          </div>
        </DragDropContext>
        {noOfPage > 1 ? (
          <Pagination handlePageClick={handlePageClick} noOfPage={noOfPage} startDataCount={startDataCount} endDataCount={endDataCount} count={totalDataCount} forcePage={currentPage} />
        ) : (
          <div className="flex justify-center sm:justify-between my-4 text-sm text-black text-center sm:text-left">
            {totalDataCount > 0 ? (
              <div>
                <p>
                  {t("showing")}: {startDataCount} to {endDataCount} of {totalDataCount}
                </p>
              </div>
            ) : (
              ""
            )}
          </div>
        )}
      </div>
      {isOpen?.open && <AddEditPlanFeaturePopup isOpen={isOpen?.open} id={isOpen?._id} setIsOpen={setIsopen} loadList={loadList} />}
    </>
  );
};
export default PlanFeatures;
